/**
 * Catalogs, types and helpers for the rich user profile + card visit
 * (user-detail.md). This is the `userDetail.ts` the spec refers to. The backend
 * keeps a parallel copy in `src/handlers/portal-catalogs.ts` — keep them in sync.
 */
import type { SelectOption } from '../components/ui';

/** Nhóm B/C — "LĨNH VỰC" (stats[2]). */
export const DOMAINS = [
  'Chiến lược', 'Product', 'Platform', 'Brand', 'Finance', 'People',
  'Cloud', 'Ops', 'Design', 'Data', 'Sales', 'Legal', 'R&D', 'Security',
] as const;

/** Nhóm C — "Chỉ số chính" (stats[1]): label -> suffix appended to the value. */
export const MID_STATS: Record<string, string> = {
  'DỰ ÁN': '+',
  'HỆ THỐNG': '',
  'CHIẾN DỊCH': '+',
  'SÁNG KIẾN': '+',
  'GIẢI PHÁP': '',
};

export const DOMAIN_OPTIONS: SelectOption[] = DOMAINS.map((d) => ({ value: d, label: d }));
export const MID_STAT_OPTIONS: SelectOption[] = Object.keys(MID_STATS).map((k) => ({
  value: k,
  label: k,
}));

export function midStatSuffix(label: string): string {
  return MID_STATS[label] ?? '';
}

// -- Card visit (Nhóm D) ----------------------------------------------------
export interface CardFieldValue {
  value: string;
  visible: boolean;
}
export interface CardSocialValue {
  platform: string;
  url: string;
  visible: boolean;
}
export interface CardVisit {
  email?: CardFieldValue;
  phone?: CardFieldValue;
  website?: CardFieldValue;
  linkedin?: CardFieldValue;
  address?: CardFieldValue;
  bio?: CardFieldValue;
  socials?: CardSocialValue[];
}

/** Public projection returned by GET /api/public/users/{handle}. */
export interface PublicCard {
  handle: string | null;
  name: string | null;
  initials: string | null;
  photo: string | null;
  title: string | null;
  company: string | null;
  tagline: string | null;
  domain: string | null;
  stats: { label: string; value: string }[];
  card: {
    email?: string;
    phone?: string;
    website?: string;
    linkedin?: string;
    address?: string;
    bio?: string;
    socials?: { platform: string; url: string }[];
  };
}

/**
 * Tiered privacy defaults (user-detail.md): professional/identity fields are public
 * by default; direct-contact PII (email/phone/address) starts hidden until opted in.
 */
export const DEFAULT_VISIBILITY: Record<keyof Omit<CardVisit, 'socials'>, boolean> = {
  email: false,
  phone: false,
  address: false,
  website: true,
  linkedin: true,
  bio: true,
};

/** Builds a downloadable vCard (.vcf) from the public card's visible fields. */
export function buildVCard(pub: PublicCard): string {
  const lines = ['BEGIN:VCARD', 'VERSION:3.0'];
  if (pub.name) lines.push(`FN:${escapeVCard(pub.name)}`);
  if (pub.title) lines.push(`TITLE:${escapeVCard(pub.title)}`);
  if (pub.company) lines.push(`ORG:${escapeVCard(pub.company)}`);
  if (pub.card.email) lines.push(`EMAIL;TYPE=WORK:${pub.card.email}`);
  if (pub.card.phone) lines.push(`TEL;TYPE=CELL:${pub.card.phone}`);
  if (pub.card.website) lines.push(`URL:${pub.card.website}`);
  if (pub.card.linkedin) lines.push(`URL;TYPE=LinkedIn:${pub.card.linkedin}`);
  if (pub.card.address) lines.push(`ADR;TYPE=WORK:;;${escapeVCard(pub.card.address)};;;;`);
  if (pub.card.bio) lines.push(`NOTE:${escapeVCard(pub.card.bio)}`);
  for (const s of pub.card.socials ?? []) lines.push(`URL;TYPE=${escapeVCard(s.platform)}:${s.url}`);
  lines.push('END:VCARD');
  return lines.join('\r\n');
}

function escapeVCard(value: string): string {
  return value.replace(/([,;\\])/g, '\\$1').replace(/\n/g, '\\n');
}
